import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Sparkles, Zap, Code, Users, ArrowRight, CheckCircle, Calendar, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useEmailCollection, EMAIL_COLLECTION_CONFIGS } from '@/hooks/useEmailCollection';
import SEOHead from '@/components/SEOHead';

export default function ImageMemoriesPage() {
  const [email, setEmail] = useState('');
  const { trackEvent } = useAnalytics();
  
  // Use Customer.io email collection hook
  const { submitEmail, isSubmitting, isSuccess, error } = useEmailCollection(EMAIL_COLLECTION_CONFIGS.IMAGE_MEMORIES);

  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Track beta interest
    trackEvent('roadmap_feature_interest', {
      feature: 'image_memories',
      email_provided: true,
      source: 'roadmap_landing_page'
    });

    // Submit to Customer.io
    const success = await submitEmail(email);
    if (success) {
      setEmail(''); // Clear form on success
    }
  };

  const handleFeatureClick = (feature: string) => {
    trackEvent('roadmap_feature_detail_view', {
      feature: 'image_memories',
      detail_section: feature,
      source: 'roadmap_landing_page'
    });
  };

  return (
    <>
      <SEOHead 
        title="Visual AI Memory Storage - Image Memories"
        description="Add visual context to your AI applications with Memara's image memory storage. Semantic image search, OCR text extraction, and visual AI understanding for GPT developers and AI engineers."
        keywords={["AI image memory", "visual memory storage", "GPT image context", "AI image search", "semantic image search", "OCR API", "visual AI development", "multimodal AI memory", "image embeddings", "AI developer tools"]}
        canonicalUrl="https://memara.io/roadmap/image-memories"
        ogType="article"
      />

      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        {/* Hero Section */}
        <section className="relative px-6 py-24 mx-auto max-w-7xl lg:px-8">
          <div className="text-center">
            <Badge className="mb-6 bg-purple-100 text-purple-700 hover:bg-purple-200">
              <Calendar className="w-4 h-4 mr-2" />
              Images coming soon, get early access
            </Badge>
            
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              Visual AI Memory Storage
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">
                for Smart Applications
              </span>
            </h1>
            
            <p className="mt-6 text-xl leading-8 text-gray-600 max-w-3xl mx-auto">
              <Link to="#image-interest" className="font-medium underline">
                Images coming soon, get early access
              </Link>
              . Planned work includes semantic image search and OCR.
            </p>

            <div className="mt-10 flex items-center justify-center gap-x-6">
              <div id="image-interest" className="bg-white rounded-lg shadow-lg p-6 max-w-md scroll-mt-24">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Bell className="w-5 h-5 mr-2 text-purple-600" />
                  Get Early Access
                </h3>
                
                {!isSuccess ? (
                  <form onSubmit={handleEmailSignup} className="space-y-4">
                    <Input
                      type="email"
                      placeholder="your.email@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isSubmitting}
                      className="w-full"
                    />
                    {error && (
                      <p className="text-sm text-red-600">{error}</p>
                    )}
                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                    >
                      {isSubmitting ? 'Submitting...' : 'Notify Me When Available'}
                      {!isSubmitting && <ArrowRight className="w-4 h-4 ml-2" />}
                    </Button>
                  </form>
                ) : (
                  <div className="text-center py-4">
                    <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-green-700 font-medium">You're on the list!</p>
                    <p className="text-sm text-gray-600">We'll notify you when image memories launch.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Technical Capabilities */}
        <section className="px-6 py-16 mx-auto max-w-7xl lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              AI-Powered Visual Memory Capabilities
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Built for developers who need intelligent image processing and semantic search
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleFeatureClick('semantic_search')}>
              <CardHeader>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle>Semantic Image Search</CardTitle>
                <CardDescription>
                  Find images by describing what you're looking for, not just file names
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• AI-powered image understanding with CLIP embeddings</li>
                  <li>• Search by objects, scenes, and visual concepts</li>
                  <li>• Cross-modal search: text queries → find relevant images</li>
                  <li>• Similarity matching for visual content discovery</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleFeatureClick('ocr_extraction')}>
              <CardHeader>
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                  <Camera className="w-6 h-6 text-indigo-600" />
                </div>
                <CardTitle>OCR Text Extraction</CardTitle>
                <CardDescription>
                  Automatically extract and index text content from images
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• High-accuracy text recognition for documents</li>
                  <li>• Multi-language OCR support</li>
                  <li>• Searchable text content from screenshots</li>
                  <li>• Structured data extraction from forms and tables</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleFeatureClick('ai_analysis')}>
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>AI Visual Analysis</CardTitle>
                <CardDescription>
                  Intelligent content analysis and automatic tagging
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Object and scene detection</li>
                  <li>• Automatic alt-text generation for accessibility</li>
                  <li>• Content moderation and safety filtering</li>
                  <li>• Visual sentiment and emotion analysis</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Developer Integration Examples */}
        <section className="px-6 py-16 mx-auto max-w-7xl lg:px-8 bg-gray-50">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Built for AI Developers
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Simple APIs that integrate seamlessly with your existing AI workflows
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <Code className="w-6 h-6 text-purple-600" />
                  <CardTitle>Store Visual Memories</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-900 rounded-lg p-4 text-sm">
                  <pre className="text-green-400">
{`// Store image with context
await memara.createMemory({
  content: {
    text: "Product wireframe from client meeting",
    images: [wireframe_screenshot]
  },
  tags: ["wireframe", "client", "product"],
  space: "work_projects"
});`}
                  </pre>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <Sparkles className="w-6 h-6 text-indigo-600" />
                  <CardTitle>Semantic Visual Search</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-900 rounded-lg p-4 text-sm">
                  <pre className="text-green-400">
{`// Find images by description
const results = await memara.searchMemories({
  query: "wireframes with login screens",
  contentTypes: ["images"],
  space: "work_projects"
});`}
                  </pre>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Use Cases for AI Developers */}
        <section className="px-6 py-16 mx-auto max-w-7xl lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Perfect for AI Development Teams
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Real-world use cases that enhance your AI applications
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Client Demo Enhancement</h3>
                  <p className="text-gray-600">
                    Store client wireframes, mockups, and feedback screenshots. 
                    Your AI can instantly recall visual context during presentations.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Code className="w-4 h-4 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Documentation Intelligence</h3>
                  <p className="text-gray-600">
                    OCR extraction from technical diagrams and architecture screenshots. 
                    Make visual documentation searchable and AI-accessible.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Zap className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Visual Context for GPTs</h3>
                  <p className="text-gray-600">
                    Enhance Custom GPTs with visual memory capabilities. 
                    Store UI examples, error screenshots, and design references.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Camera className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Bug Report Intelligence</h3>
                  <p className="text-gray-600">
                    Store error screenshots with automatic OCR of error messages. 
                    AI can correlate visual bugs with similar past issues.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Design System Memory</h3>
                  <p className="text-gray-600">
                    Visual component library with semantic search. 
                    Find UI patterns by describing functionality, not file names.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Team Knowledge Sharing</h3>
                  <p className="text-gray-600">
                    Shared visual memory spaces for development teams. 
                    Collaborative image libraries with AI-powered organization.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Integration Platforms */}
        <section className="px-6 py-16 mx-auto max-w-7xl lg:px-8 bg-gray-50">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Works with Your Favorite Platforms
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Image memories will be available across all Memara integrations
            </p>
          </div>

          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-lg shadow-md flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🤖</span>
              </div>
              <h3 className="font-medium">Claude Desktop</h3>
              <p className="text-sm text-gray-500">
                <Link to="#image-interest" className="underline">
                  Images coming soon, get early access
                </Link>
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-lg shadow-md flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💬</span>
              </div>
              <h3 className="font-medium">Slack/Discord</h3>
              <p className="text-sm text-gray-500">
                <Link to="#image-interest" className="underline">
                  Images coming soon, get early access
                </Link>
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-lg shadow-md flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="font-medium">Zapier/Make</h3>
              <p className="text-sm text-gray-500">Automation workflows</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-lg shadow-md flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔧</span>
              </div>
              <h3 className="font-medium">API/MCP</h3>
              <p className="text-sm text-gray-500">Full developer access</p>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 mt-8">
            *Claude Desktop will show image context awareness but cannot display images directly
          </p>
        </section>

        {/* CTA Section */}
        <section className="px-6 py-24 mx-auto max-w-7xl lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-6">
              Ready to Enhance Your AI Applications?
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Join hundreds of AI developers building smarter applications with Memara's memory platform.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-purple-600 hover:bg-purple-700">
                Start Building with Text Memories
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline">
                View Technical Documentation
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
} 